/** 앱 — every app in one table (S2, `/apps`).
 *
 *  The list of apps lived in four places with different columns: the old Apps
 *  table (country, source, bundle), the Tracking page's app cards (rank
 *  distribution, best rank, last snapshot, average popularity and difficulty),
 *  the dashboard scoreboard and the Compare table. This is the one table for
 *  all of them. It joins three answers by app id — `listApps`, `trackingOverview`
 *  (my apps only) and `listAnalyses(true)` (mine and competitors) — so a
 *  competitor row simply has no rank columns.
 *
 *  Two things go out to other services and are never pressed in a dev server:
 *  the ASC import and the iTunes search / track. */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Boxes, CloudDownload, Search, Trash2 } from 'lucide-react'
import { api, type AnalysisRow, type App, type ITunesResult, type TrackingOverviewRow } from '@/api/client'
import { qk, useAnalyses, useApps, useTrackingOverview } from '@/lib/queries'
import { formatDay } from '@/lib/time'
import {
  AppIcon,
  Button,
  IconButton,
  ConfirmDialog,
  Legend,
  CountryTag,
  DataTable,
  Disclosure,
  EmptyState,
  errorText,
  ErrorState,
  Field,
  FilterBar,
  FilterSelect,
  Input,
  MiniBar,
  PageHeader,
  rankSegments,
  ScoreNumber,
  SearchInput,
  Segmented,
  Select,
  StackedBar,
  StatusPill,
  useQueryParam,
  useSetQueryParams,
  useToast,
  type Column,
} from '@/ui'

export interface AppRow {
  app: App
  overview?: TrackingOverviewRow
  analysis?: AnalysisRow
  keywordCount: number
  score: number
}

/** Joins the three answers on the app id. The overview and the analysis list
 *  may each lack an app (a competitor has no overview row; an app with no
 *  analysis yet has no score). */
export function joinApps(apps: App[], overview: TrackingOverviewRow[], analyses: AnalysisRow[]): AppRow[] {
  const ov = new Map(overview.map((o) => [o.app.id, o]))
  const an = new Map(analyses.map((a) => [a.app.id, a]))
  // `listApps` answers my apps only; competitors that have an analysis come in
  // through the analysis list, so the 경쟁사 filter has rows to show.
  const known = new Set(apps.map((a) => a.id))
  const extra = analyses.map((a) => a.app).filter((a, i, all) => !known.has(a.id) && all.findIndex((b) => b.id === a.id) === i)
  return [...apps, ...extra].map((app) => {
    const overviewRow = ov.get(app.id)
    const analysis = an.get(app.id)
    return {
      app,
      overview: overviewRow,
      analysis,
      keywordCount: overviewRow?.keyword_count ?? analysis?.keyword_count ?? 0,
      score: overviewRow?.analysis_score || analysis?.analysis.overall_score || 0,
    }
  })
}

type Kind = 'all' | 'mine' | 'competitor'
type SortKey = 'score' | 'keywords' | 'rank' | 'name'

const KINDS: { value: Kind; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'mine', label: '내 앱' },
  { value: 'competitor', label: '경쟁사' },
]

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'score', label: '점수' },
  { value: 'keywords', label: '키워드 수' },
  { value: 'rank', label: '최고 순위' },
  { value: 'name', label: '이름' },
]

const asKind = (v: string): Kind => (v === 'mine' || v === 'competitor' ? v : 'all')
const asSort = (v: string): SortKey => (SORTS.some((s) => s.value === v) ? (v as SortKey) : 'score')

const bestRank = (r: AppRow): number => r.overview?.best_keyword?.rank ?? 0

/** Score, keyword count and best rank run the useful end first; a row with no
 *  value for the key goes last whatever the key. Ties keep the server's order. */
export function sortApps(rows: AppRow[], key: SortKey): AppRow[] {
  const value = (r: AppRow): number | string | null => {
    switch (key) {
      case 'score':
        return r.score > 0 ? -r.score : null
      case 'keywords':
        return r.keywordCount > 0 ? -r.keywordCount : null
      case 'rank':
        return bestRank(r) > 0 ? bestRank(r) : null
      case 'name':
        return r.app.name.toLowerCase()
    }
  }
  return rows
    .map((r, i) => ({ r, i, v: value(r) }))
    .sort((a, b) => {
      if (a.v === null && b.v === null) return a.i - b.i
      if (a.v === null) return 1
      if (b.v === null) return -1
      const c = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : String(a.v).localeCompare(String(b.v))
      return c || a.i - b.i
    })
    .map((x) => x.r)
}

function sourceLabel(source: string): string {
  if (source === 'asc') return 'ASC'
  if (source === 'itunes') return 'iTunes'
  return source || '—'
}

export default function AppList() {
  const toast = useToast()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const setParams = useSetQueryParams()
  const apps = useApps()
  const overview = useTrackingOverview()
  const analyses = useAnalyses(true)

  const [q] = useQueryParam('q')
  const [kindParam] = useQueryParam('kind', 'all')
  const [sortParam] = useQueryParam('sort', 'score')
  const kind = asKind(kindParam)
  const sort = asSort(sortParam)

  const all = useMemo(
    () => joinApps(apps.data?.items ?? [], overview.data?.items ?? [], analyses.data?.items ?? []),
    [apps.data, overview.data, analyses.data],
  )
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const matching = all.filter(
      (r) =>
        (kind === 'all' || (kind === 'mine') === r.app.is_mine) &&
        (!needle || r.app.name.toLowerCase().includes(needle) || r.app.bundle_id.toLowerCase().includes(needle) || r.app.developer.toLowerCase().includes(needle)),
    )
    return sortApps(matching, sort)
  }, [all, q, kind, sort])
  const filtered = !!(q || kind !== 'all')

  const refresh = () => {
    qc.invalidateQueries({ queryKey: qk.apps })
    qc.invalidateQueries({ queryKey: qk.trackingOverview })
    qc.invalidateQueries({ queryKey: ['analyses'] })
    qc.invalidateQueries({ queryKey: qk.dashboard })
  }

  const importASC = useMutation({
    mutationFn: api.importASC,
    onSuccess: (r) => {
      refresh()
      toast.success(`ASC에서 앱 ${r.imported?.length ?? 0}개를 가져왔다 (ASC ${r.asc_count}개 중)`)
    },
    onError: (e) => toast.error(errorText(e)),
  })

  const [toDelete, setToDelete] = useState<App | null>(null)
  const delApp = useMutation({
    mutationFn: (id: number) => api.deleteApp(id),
    onSuccess: () => {
      refresh()
      toast.success('앱을 삭제했다')
      setToDelete(null)
    },
    onError: (e) => toast.error(errorText(e)),
  })

  const uniformKind = rows.every((r) => r.app.is_mine === rows[0]?.app.is_mine && r.app.source === rows[0]?.app.source)
  const newestSnapshot = rows.reduce((m, r) => ((r.overview?.last_snapshot ?? '') > m ? (r.overview?.last_snapshot ?? '') : m), '')

  const columns: Column<AppRow>[] = [
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (r) => (
        // Capped so a long store name truncates instead of widening the
        // table past the screen (production names run to 30+ characters).
        <span className="flex min-w-0 max-w-[18rem] items-center gap-2.5">
          <AppIcon src={r.app.icon_url} name={r.app.name} size={32} />
          <span className="min-w-0">
            <span className="block truncate font-medium">{r.app.name}</span>
            <span className="block truncate text-meta text-muted-foreground">{r.app.developer || '—'}</span>
          </span>
        </span>
      ),
    },
    { key: 'country', header: '국가', width: '64px', card: 'meta', cell: (r) => <CountryTag code={r.app.country} /> },
    {
      key: 'kind',
      header: '구분',
      width: '120px',
      card: 'line',
      // a phone card says it only when the list mixes kinds
      cardHide: () => uniformKind,
      cell: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <StatusPill domain="app" status={r.app.is_mine ? 'mine' : 'competitor'} />
          <span className="text-meta text-muted-foreground">{sourceLabel(r.app.source)}</span>
        </span>
      ),
    },
    {
      key: 'keywords',
      header: '키워드',
      width: '72px',
      align: 'right',
      className: 'tabular-nums',
      card: 'hidden',
      cardHide: (r) => !r.overview,
      cell: (r) => (r.overview ? r.keywordCount.toLocaleString('ko-KR') : <span className="text-muted-foreground">—</span>),
    },
    {
      key: 'dist',
      header: '순위 분포',
      width: '140px',
      card: 'hidden',
      cell: (r) =>
        r.overview && r.keywordCount > 0 ? (
          <span className="block w-full min-w-[96px] max-w-[160px]">
            <StackedBar
              segments={rankSegments({ top_10: r.overview.top_10, top_50: r.overview.top_50, ranked_50_plus: r.overview.ranked_50_plus, unranked: r.overview.unranked })}
              label={`${r.app.name} 순위 분포: Top 10 ${r.overview.top_10}, 11–50 ${r.overview.top_50}, 51+ ${r.overview.ranked_50_plus}, 순위 밖 ${r.overview.unranked}`}
            />
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      // the phone's one line for the count and its spread
      key: 'kwdist',
      header: '키워드',
      cardOnly: true,
      cardHide: (r) => !r.overview,
      cell: (r) =>
        r.overview ? (
          <span className="flex min-w-0 items-center gap-2.5">
            <span className="tabular-nums">{r.keywordCount.toLocaleString('ko-KR')}</span>
            {r.keywordCount > 0 && (
              <span className="block w-full min-w-[96px] max-w-[160px]">
                <StackedBar
                  segments={rankSegments({ top_10: r.overview.top_10, top_50: r.overview.top_50, ranked_50_plus: r.overview.ranked_50_plus, unranked: r.overview.unranked })}
                  label={`${r.app.name} 순위 분포: Top 10 ${r.overview.top_10}, 11–50 ${r.overview.top_50}, 51+ ${r.overview.ranked_50_plus}, 순위 밖 ${r.overview.unranked}`}
                />
              </span>
            )}
          </span>
        ) : null,
    },
    {
      key: 'best',
      header: '최고 순위',
      card: 'line',
      cardHide: (r) => !r.overview,
      cell: (r) => {
        const b = r.overview?.best_keyword
        return b ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <span className="font-semibold tabular-nums">#{b.rank}</span>
            <span className="min-w-0 max-w-[10rem] truncate">{b.term}</span>
            <CountryTag code={b.country} />
          </span>
        ) : (
          <span className="text-muted-foreground">{r.overview ? '순위 밖' : '—'}</span>
        )
      },
    },
    { key: 'score', header: '점수', width: '64px', align: 'right', card: 'meta', cell: (r) => <ScoreNumber value={r.score} /> },
    {
      key: 'pop',
      header: '평균 인기도',
      width: '112px',
      card: 'hidden',
      cell: (r) => <MiniBar label="평균 인기도" value={r.overview?.avg_popularity ?? 0} fill="bg-chart-1" />,
    },
    {
      key: 'diff',
      header: '평균 난이도',
      width: '112px',
      card: 'hidden',
      cell: (r) => <MiniBar label="평균 난이도" value={r.overview?.avg_difficulty ?? 0} fill="bg-chart-2" />,
    },
    {
      key: 'measures',
      header: '평균 인기 · 난이도',
      cardOnly: true,
      cardHide: (r) => !r.overview?.avg_popularity && !r.overview?.avg_difficulty,
      cell: (r) => (
        <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-1">
          <MiniBar label="평균 인기도" value={r.overview?.avg_popularity ?? 0} fill="bg-chart-1" />
          <MiniBar label="평균 난이도" value={r.overview?.avg_difficulty ?? 0} fill="bg-chart-2" />
        </span>
      ),
    },
    {
      key: 'snap',
      header: '마지막 스냅샷',
      width: '116px',
      className: 'whitespace-nowrap tabular-nums text-muted-foreground',
      card: 'line',
      // on a card only for an app whose last snapshot is behind the rest
      cardHide: (r) => !r.overview?.last_snapshot || r.overview.last_snapshot === newestSnapshot,
      cell: (r) => formatDay(r.overview?.last_snapshot),
    },
    {
      key: 'actions',
      header: <span className="sr-only">처리</span>,
      width: '56px',
      align: 'right',
      card: 'actions',
      cell: (r) => (
        <span
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <IconButton icon={Trash2} size="sm" variant="quietDanger" label={`${r.app.name} 삭제`} onClick={() => setToDelete(r.app)} />
        </span>
      ),
    },
  ]

  const loading = apps.isLoading
  const error = apps.error

  return (
    <>
      <PageHeader
        title="앱"
        description="추적하는 앱과 경쟁사를 한 표에 모았다. 행을 누르면 그 앱의 키워드를 연다."
        actions={
          <Button variant="primary" icon={CloudDownload} loading={importASC.isPending} onClick={() => importASC.mutate()}>
            ASC에서 가져오기
          </Button>
        }
      >
        <FilterBar end={<span className="text-table tabular-nums text-muted-foreground">{rows.length}개</span>}>
          <SearchInput value={q} onChange={(v) => setParams({ q: v })} placeholder="이름·번들 ID 검색" />
          <Segmented label="구분" size="sm" value={kind} onChange={(v) => setParams({ kind: v === 'all' ? '' : v })} options={KINDS} />
          <FilterSelect
            label="정렬"
            value={sort}
            onChange={(v) => setParams({ sort: v === 'score' ? '' : v })}
            options={SORTS.map((s) => ({ value: s.value, label: `정렬: ${s.label}` }))}
          />
        </FilterBar>
      </PageHeader>

      {importASC.error && <ErrorState className="mb-4" title="ASC에서 가져오지 못했다" error={importASC.error} />}

      <div className="space-y-6">
        <div className="space-y-2">
          {rows.some((r) => r.overview && r.keywordCount > 0) && (
            <Legend items={rankSegments({ top_10: 0, top_50: 0, ranked_50_plus: 0, unranked: 0 }).map((g) => ({ label: g.label, swatch: g.fill }))} />
          )}
          <DataTable
            label="앱"
            columns={columns}
            rows={rows}
            rowKey={(r) => r.app.id}
            loading={loading}
            error={error}
            onRetry={() => apps.refetch()}
            onRowClick={(r) => navigate(`/apps/${r.app.id}/keywords`)}
            empty={
              all.length === 0 ? (
                <EmptyState
                  icon={Boxes}
                  title="앱이 아직 없다"
                  description="App Store Connect에서 내 앱을 가져오거나, 아래에서 iTunes로 찾아 추적에 넣는다."
                  actions={
                    <Button variant="primary" icon={CloudDownload} loading={importASC.isPending} onClick={() => importASC.mutate()}>
                      ASC에서 가져오기
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  title="조건에 맞는 앱이 없다"
                  description="필터를 풀면 더 보인다."
                  actions={filtered ? <Button onClick={() => setParams({ q: '', kind: '' })}>필터 지우기</Button> : undefined}
                />
              )
            }
          />
        </div>

        <ItunesFinder onTracked={refresh} />
      </div>

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={`“${toDelete?.name ?? ''}” 앱을 삭제할까`}
        description="키워드와 순위 이력, 경쟁사 연결, 점수 분석이 함께 지워진다."
        confirmLabel="삭제"
        confirmVariant="destructive"
        loading={delApp.isPending}
        onConfirm={async () => {
          if (!toDelete) return
          try {
            await delApp.mutateAsync(toDelete.id)
          } catch {
            /* the toast already says why */
          }
        }}
      />
    </>
  )
}

// ── iTunes에서 찾기 ─────────────────────────────────────────────────────────

const COUNTRIES = ['us', 'kr', 'jp', 'cn', 'gb', 'de']

/** Search the App Store by name or developer and put an app on the tracking
 *  list. Folded: the table above is the page; this is how an app gets into it
 *  when it is not in App Store Connect (a competitor). */
function ItunesFinder({ onTracked }: { onTracked: () => void }) {
  const toast = useToast()
  const [term, setTerm] = useState('')
  const [country, setCountry] = useState('us')
  const [searched, setSearched] = useState({ term: '', country: 'us' })
  const search = useQuery({
    queryKey: ['itunes-search', searched.term, searched.country],
    queryFn: () => api.searchItunes(searched.term, searched.country),
    enabled: searched.term.length > 1,
  })
  const track = useMutation({
    mutationFn: (r: ITunesResult) => api.importItunes(r.trackId, searched.country),
    onSuccess: (_, r) => {
      onTracked()
      toast.success(`“${r.trackName}”을(를) 추적에 넣었다`)
    },
    onError: (e) => toast.error(errorText(e)),
  })

  const results = (search.data?.items ?? []).slice(0, 20)
  const columns: Column<ITunesResult>[] = [
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <AppIcon src={r.artworkUrl100} name={r.trackName} size={32} />
          <span className="min-w-0">
            <span className="block truncate font-medium">{r.trackName}</span>
            <span className="block truncate text-meta text-muted-foreground">{r.sellerName}</span>
          </span>
        </span>
      ),
    },
    { key: 'genre', header: '카테고리', card: 'sub', cell: (r) => r.primaryGenreName },
    {
      key: 'rating',
      header: '평점',
      align: 'right',
      className: 'whitespace-nowrap tabular-nums',
      card: 'line',
      cell: (r) =>
        typeof r.averageUserRating === 'number' ? (
          <span>
            ★ {r.averageUserRating.toFixed(1)} <span className="text-muted-foreground">({(r.userRatingCount ?? 0).toLocaleString('ko-KR')})</span>
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">추적</span>,
      width: '84px',
      align: 'right',
      card: 'actions',
      cell: (r) => (
        <Button size="sm" aria-label={`${r.trackName} 추적`} disabled={track.isPending} onClick={() => track.mutate(r)}>
          추적
        </Button>
      ),
    },
  ]

  return (
    <Disclosure title="iTunes에서 찾기" hint="이름이나 개발사로 앱을 찾아 추적에 넣는다">
      <form
        aria-label="iTunes 검색"
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setSearched({ term: term.trim(), country })
        }}
      >
        <Field label="앱 이름 또는 개발사" className="min-w-[14rem] flex-1">
          <Input placeholder="예: Duolingo" value={term} onChange={(e) => setTerm(e.target.value)} />
        </Field>
        <Field label="국가" className="w-24">
          <Select value={country} onChange={(e) => setCountry(e.target.value)}>
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {c.toUpperCase()}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" icon={Search} loading={search.isFetching} disabled={term.trim().length < 2} className="self-start sm:self-end">
          찾기
        </Button>
      </form>
      {searched.term.length < 2 ? (
        <EmptyState title="아직 찾지 않았다" description="이름이나 개발사를 두 글자 이상 넣고 찾기를 누른다. 누르기 전에는 iTunes를 부르지 않는다." />
      ) : (
        <DataTable
          label="iTunes 검색 결과"
          columns={columns}
          rows={results}
          rowKey={(r) => r.trackId}
          loading={search.isLoading}
          error={search.error}
          onRetry={() => search.refetch()}
          empty={<EmptyState title="결과가 없다" description="철자를 바꾸거나 국가를 바꿔 다시 찾는다." />}
        />
      )}
    </Disclosure>
  )
}
